import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
    Receipt,
    Search,
    Eye,
    Edit,
    Trash2,
    DollarSign,
    Calendar,
    Plus,
    Loader2,
    AlertCircle,
    CreditCard,
    RefreshCw,
    X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { payoutService } from '@/services/payoutService'
import { SettlementItem } from '@/types'
import { safeFormatDistanceToNow } from '@/utils/date'
import { useAuth } from '@/hooks/useAuth'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'

export default function AdminSettlementItemsList() {
    const navigate = useNavigate()
    const { settlementId } = useParams<{ settlementId: string }>()
    const { user } = useAuth()
    const { branding } = useBranding()
    const pageStyles = getPageStyles(branding)
    const cardStyles = getCardStyles(branding)
    const statusColors = getStatusColors(branding)
    const [settlementItems, setSettlementItems] = useState<SettlementItem[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [pageSize] = useState(20)
    const [buildingItems, setBuildingItems] = useState(false)
    const [showCreateForm, setShowCreateForm] = useState(false)
    const [creatingItem, setCreatingItem] = useState(false)
    const [createFormData, setCreateFormData] = useState({
        transaction: '',
        amount_fiat: '',
        commission_rate: '',
        commission_amount: '',
        net_amount: ''
    })

    useEffect(() => {
        if (settlementId) {
            fetchSettlementItems()
        }
    }, [settlementId, currentPage])

    const fetchSettlementItems = async () => {
        if (!settlementId) return

        try {
            setLoading(true)
            setError(null)

            const response = await payoutService.getSettlementItemsBySettlement(parseInt(settlementId))

            if (response.success) {
                setSettlementItems(response.data)
                setTotalCount(response.data.length)
            } else {
                setError('Failed to fetch settlement items')
            }
        } catch (err) {
            setError('An error occurred while fetching settlement items')
            console.error('Error fetching settlement items:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleBuildFromBatch = async () => {
        if (!settlementId) return

        try {
            setBuildingItems(true)
            setError(null)

            const response = await payoutService.buildSettlementItemsFromBatch(parseInt(settlementId))

            if (response.success) {
                // Refresh the list
                await fetchSettlementItems()
            } else {
                setError('Failed to build settlement items from batch')
            }
        } catch (err) {
            setError('An error occurred while building settlement items')
            console.error('Error building settlement items:', err)
        } finally {
            setBuildingItems(false)
        }
    }

    const handleDeleteItem = async (id: number) => {
        if (!confirm('Are you sure you want to delete this settlement item?')) return

        try {
            const response = await payoutService.deleteSettlementItem(id)
            if (response.success) {
                await fetchSettlementItems()
            } else {
                setError('Failed to delete settlement item')
            }
        } catch (err) {
            setError('An error occurred while deleting settlement item')
            console.error('Error deleting settlement item:', err)
        }
    }

    const handleCreateItem = async () => {
        if (!settlementId) return

        try {
            setCreatingItem(true)
            setError(null)

            const itemData = {
                settlement: parseInt(settlementId),
                transaction: parseInt(createFormData.transaction),
                amount_fiat: parseFloat(createFormData.amount_fiat),
                commission_rate: parseFloat(createFormData.commission_rate),
                commission_amount: parseFloat(createFormData.commission_amount),
                net_amount: parseFloat(createFormData.net_amount)
            }

            const response = await payoutService.createSettlementItem(itemData)

            if (response.success) {
                // Reset form and refresh list
                setCreateFormData({
                    transaction: '',
                    amount_fiat: '',
                    commission_rate: '',
                    commission_amount: '',
                    net_amount: ''
                })
                setShowCreateForm(false)
                await fetchSettlementItems()
            } else {
                setError('Failed to create settlement item')
            }
        } catch (err) {
            setError('An error occurred while creating settlement item')
            console.error('Error creating settlement item:', err)
        } finally {
            setCreatingItem(false)
        }
    }

    const handleFormInputChange = (field: string, value: string) => {
        setCreateFormData(prev => ({ ...prev, [field]: value }))

        // Auto-calculate net amount if amount_fiat and commission_amount are provided
        if (field === 'amount_fiat' || field === 'commission_amount') {
            const amount = field === 'amount_fiat' ? parseFloat(value) : parseFloat(createFormData.amount_fiat)
            const commission = field === 'commission_amount' ? parseFloat(value) : parseFloat(createFormData.commission_amount)

            if (!isNaN(amount) && !isNaN(commission)) {
                const netAmount = amount - commission
                setCreateFormData(prev => ({ ...prev, net_amount: netAmount.toString() }))
            }
        }
    }

    const filteredItems = settlementItems.filter(item =>
        item.transaction_reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.transaction_currency.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const totalPages = Math.ceil(totalCount / pageSize)

    if (loading && settlementItems.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={pageStyles.container}>
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: branding.primaryColor }} />
                    <p style={pageStyles.textSecondary}>Loading settlement items...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen" style={pageStyles.container}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div>
                                <h1 className="text-3xl font-bold" style={pageStyles.text}>Settlement Items</h1>
                                <p className="mt-1" style={pageStyles.textSecondary}>Manage individual transaction items within settlement #{settlementId}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <Button
                                onClick={() => navigate('/admin/settlements')}
                                variant="outline"
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                                className="hover:opacity-80"
                            >
                                Back to Settlements
                            </Button>
                            {user && (user.role === 'admin' || user.is_superuser) && (
                                <Button
                                    onClick={() => setShowCreateForm(true)}
                                    style={{ backgroundColor: statusColors.success.backgroundColor }}
                                    className="hover:opacity-80"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Create Item
                                </Button>
                            )}
                            <Button
                                onClick={handleBuildFromBatch}
                                disabled={buildingItems}
                                style={{ backgroundColor: branding.primaryColor }}
                                className="hover:opacity-80"
                            >
                                {buildingItems ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Building...
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                        Build from Batch
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="rounded-lg p-4 mb-6" style={{
                        backgroundColor: `${statusColors.error.backgroundColor}20`,
                        borderColor: `${statusColors.error.color}50`
                    }}>
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 mr-2" style={{ color: statusColors.error.color }} />
                            <p style={{ color: statusColors.error.color }}>{error}</p>
                        </div>
                    </div>
                )}

                {/* Create Settlement Item Form Modal */}
                {showCreateForm && (
                    <div className="mb-6 rounded-lg shadow-sm p-6" style={cardStyles}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold" style={pageStyles.text}>Create New Settlement Item</h3>
                            <Button
                                onClick={() => setShowCreateForm(false)}
                                variant="ghost"
                                size="sm"
                                style={{ color: branding.textSecondaryColor }}
                                className="hover:opacity-80"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Transaction ID *
                                </label>
                                <Input
                                    type="number"
                                    placeholder="Enter transaction ID"
                                    value={createFormData.transaction}
                                    onChange={(e) => handleFormInputChange('transaction', e.target.value)}
                                    style={{
                                        backgroundColor: branding.surfaceColor,
                                        borderColor: `${branding.textSecondaryColor}40`,
                                        color: branding.textColor
                                    }}
                                    className="focus:ring-2"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Amount (Fiat) *
                                </label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={createFormData.amount_fiat}
                                    onChange={(e) => handleFormInputChange('amount_fiat', e.target.value)}
                                    style={{
                                        backgroundColor: branding.surfaceColor,
                                        borderColor: `${branding.textSecondaryColor}40`,
                                        color: branding.textColor
                                    }}
                                    className="focus:ring-2"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Commission Rate (%) *
                                </label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={createFormData.commission_rate}
                                    onChange={(e) => handleFormInputChange('commission_rate', e.target.value)}
                                    style={{
                                        backgroundColor: branding.surfaceColor,
                                        borderColor: `${branding.textSecondaryColor}40`,
                                        color: branding.textColor
                                    }}
                                    className="focus:ring-2"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Commission Amount *
                                </label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={createFormData.commission_amount}
                                    onChange={(e) => handleFormInputChange('commission_amount', e.target.value)}
                                    style={{
                                        backgroundColor: branding.surfaceColor,
                                        borderColor: `${branding.textSecondaryColor}40`,
                                        color: branding.textColor
                                    }}
                                    className="focus:ring-2"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Net Amount (Auto-calculated)
                                </label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={createFormData.net_amount}
                                    onChange={(e) => handleFormInputChange('net_amount', e.target.value)}
                                    style={{
                                        backgroundColor: branding.surfaceColor,
                                        borderColor: `${branding.textSecondaryColor}40`,
                                        color: branding.textColor
                                    }}
                                    className="focus:ring-2"
                                    readOnly
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-end space-x-4 mt-6">
                            <Button
                                onClick={() => setShowCreateForm(false)}
                                variant="outline"
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                                className="hover:opacity-80"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleCreateItem}
                                disabled={creatingItem || !createFormData.transaction || !createFormData.amount_fiat || !createFormData.commission_rate || !createFormData.commission_amount}
                                style={{ backgroundColor: statusColors.success.backgroundColor }}
                                className="hover:opacity-80"
                            >
                                {creatingItem ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Plus className="h-4 w-4 mr-2" />
                                        Create Item
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="rounded-lg shadow-sm p-6 mb-6" style={cardStyles}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>Search</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: branding.textSecondaryColor }} />
                                <Input
                                    placeholder="Search by transaction reference or currency..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{
                                        backgroundColor: branding.surfaceColor,
                                        borderColor: `${branding.textSecondaryColor}40`,
                                        color: branding.textColor,
                                        paddingLeft: '2.5rem'
                                    }}
                                    className="focus:ring-2"
                                />
                            </div>
                        </div>
                        <div className="flex items-end">
                            <Button
                                onClick={() => setSearchTerm('')}
                                variant="outline"
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                                className="w-full hover:opacity-80"
                            >
                                Clear Filters
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Settlement Items Table */}
                <div className="rounded-lg shadow-sm overflow-hidden" style={cardStyles}>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y" style={{ borderColor: `${branding.textSecondaryColor}30` }}>
                            <thead style={{ backgroundColor: `${branding.backgroundColor}80` }}>
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Transaction
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Amount
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Commission
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Net Amount
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Created
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y" style={{
                                backgroundColor: `${branding.surfaceColor}30`,
                                borderColor: `${branding.textSecondaryColor}30`
                            }}>
                                {filteredItems.length === 0 && !loading ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-12 text-center">
                                            <div className="flex flex-col items-center">
                                                <Receipt className="h-12 w-12 mb-4" style={{ color: branding.textSecondaryColor }} />
                                                <h3 className="text-lg font-medium mb-2" style={pageStyles.text}>No settlement items found</h3>
                                                <p className="mb-4" style={pageStyles.textSecondary}>
                                                    {searchTerm ? 'No items match your search criteria.' : 'This settlement has no items yet.'}
                                                </p>
                                                {!searchTerm && (
                                                    <Button
                                                        onClick={handleBuildFromBatch}
                                                        disabled={buildingItems}
                                                        style={{ backgroundColor: branding.primaryColor }}
                                                        className="hover:opacity-80"
                                                    >
                                                        {buildingItems ? (
                                                            <>
                                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                                Building...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Plus className="h-4 w-4 mr-2" />
                                                                Build from Batch
                                                            </>
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredItems.map((item) => (
                                        <tr key={item.id} className="hover:opacity-80" style={{ backgroundColor: `${branding.textSecondaryColor}10` }}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="p-1 rounded mr-2" style={{ backgroundColor: `${branding.primaryColor}20` }}>
                                                        <Receipt className="h-4 w-4" style={{ color: branding.primaryColor }} />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium" style={pageStyles.text}>
                                                            {item.transaction_reference}
                                                        </div>
                                                        <div className="text-sm" style={pageStyles.textSecondary}>
                                                            TXN #{item.transaction}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={pageStyles.text}>
                                                <div className="flex items-center">
                                                    <div className="p-1 rounded mr-1" style={{ backgroundColor: `${statusColors.success.backgroundColor}20` }}>
                                                        <DollarSign className="h-4 w-4" style={{ color: statusColors.success.color }} />
                                                    </div>
                                                    ${Number(item.amount_fiat).toFixed(2)} {item.transaction_currency}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={pageStyles.text}>
                                                <div className="flex items-center">
                                                    <div className="p-1 rounded mr-1" style={{ backgroundColor: `${branding.primaryColor}20` }}>
                                                        <CreditCard className="h-4 w-4" style={{ color: branding.primaryColor }} />
                                                    </div>
                                                    <div>
                                                        <div className="font-medium">
                                                            ${Number(item.commission_amount).toFixed(2)}
                                                        </div>
                                                        <div className="text-xs" style={pageStyles.textSecondary}>
                                                            {Number(item.commission_rate).toFixed(2)}%
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={pageStyles.text}>
                                                <div className="flex items-center">
                                                    <div className="p-1 rounded mr-1" style={{ backgroundColor: `${branding.secondaryColor}20` }}>
                                                        <DollarSign className="h-4 w-4" style={{ color: branding.secondaryColor }} />
                                                    </div>
                                                    ${Number(item.net_amount).toFixed(2)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={pageStyles.textSecondary}>
                                                <div className="flex items-center">
                                                    <div className="p-1 rounded mr-1" style={{ backgroundColor: `${branding.textSecondaryColor}20` }}>
                                                        <Calendar className="h-4 w-4" style={{ color: branding.textSecondaryColor }} />
                                                    </div>
                                                    {safeFormatDistanceToNow(item.created_at, 'Unknown', { addSuffix: true })}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => navigate(`/admin/settlement-items/${item.id}`)}
                                                        style={{ color: branding.textSecondaryColor }}
                                                        className="hover:opacity-80"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => navigate(`/admin/settlement-items/${item.id}/edit`)}
                                                        style={{ color: branding.textSecondaryColor }}
                                                        className="hover:opacity-80"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteItem(item.id)}
                                                        style={{ color: statusColors.error.color }}
                                                        className="hover:opacity-80"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6">
                        <div className="text-sm" style={pageStyles.textSecondary}>
                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                                className="hover:opacity-80"
                            >
                                Previous
                            </Button>
                            <span className="text-sm" style={pageStyles.textSecondary}>
                                Page {currentPage} of {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                                className="hover:opacity-80"
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
